import { cloneDeep } from 'lodash-es';
import moment from 'moment';

import { DashboardEventTypes } from '~/src/dash/analytics/analytics';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { getStartDate } from '~/src/dash/utils/date';
import { TKey } from '~/src/dash/publicaUI';

export interface ICalendarQuickSelectorOptions {
  title: string;
  tKey?: TKey;
  date1: (arg: any) => moment.Moment;
  date2: (arg: any) => moment.Moment;
}

export interface IDualCalendarProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calDisplayStartDate: moment.Moment;
  calDisplayEndDate: moment.Moment;
  compStartDate: moment.Moment;
  compEndDate: moment.Moment;
  compDisplayStartDate: moment.Moment;
  compDisplayEndDate: moment.Moment;
  calFocusedInput?: IFocusedInput;
  compFocusedInput?: IFocusedInput;
  range1Selected: string;
  range2Selected: string;
  range1DisplaySelected: string;
  range2DisplaySelected: string;
  handleChangeAggRangeSelected: (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ) => void;
  handleCalendarDateChange: (
    isCompareCalendar: boolean,
  ) => (start: moment.Moment, end: moment.Moment, isCustom: boolean) => void;
  handleDateRangeChange: (
    start: moment.Moment,
    end: moment.Moment,
    isCompareCalendar: boolean,
  ) => void;
  handleCalendarFocusedInputChange: (
    isCompareCalendar: boolean,
  ) => (focusedInput: IFocusedInput) => void;
  isComparedToEnabled: boolean;
  handleCompareEnabled: (event: any, checked: boolean) => void;
  trackEvent?: (eventTitle: DashboardEventTypes) => void;
}

export interface ICalendarProps {
  rangeSelected: string;
  rangeDisplaySelected: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  displayStartDate: moment.Moment;
  displayEndDate: moment.Moment;
  tKey?: TKey;
}

const dateRangeStrings = {
  YESTERDAY: 'Yesterday',
  THIS_WEEK_SUN_YESTERDAY: 'This Week(Sun-Yesterday)',
  THIS_WEEK_SUN_TODAY: 'This Week(Sun-Today)',
  LAST_7_DAYS: 'Last 7 Days',
  LAST_WEEK: 'Last Week(Sun-Sat)',
  THIS_MONTH: 'This Month',
  LAST_30_DAYS: 'Last 30 Days',
  LAST_MONTH: 'Last Month',
};

const {
  YESTERDAY,
  LAST_7_DAYS,
  THIS_WEEK_SUN_YESTERDAY,
  THIS_WEEK_SUN_TODAY,
  LAST_WEEK,
  THIS_MONTH,
  LAST_30_DAYS,
  LAST_MONTH,
} = dateRangeStrings;

export const generateCalendarProps = (timezone: string): ICalendarProps => {
  return {
    rangeSelected: LAST_7_DAYS,
    rangeDisplaySelected: LAST_7_DAYS,
    startDate: getStartDate(timezone, 7, 'day').startOf('day'),
    endDate: getStartDate(timezone, 1, 'day').endOf('day'),
    displayStartDate: getStartDate(timezone, 7, 'day').startOf('day'),
    displayEndDate: getStartDate(timezone, 1, 'day').endOf('day'),
  };
};

export const generateCompareCalendarProps = (timezone: string): ICalendarProps => {
  return {
    rangeSelected: 'Previous Period',
    rangeDisplaySelected: 'Compared To',
    tKey: 'ComparedTo',
    startDate: getStartDate(timezone, 14, 'day').startOf('day'),
    endDate: getStartDate(timezone, 8, 'day').endOf('day'),
    displayStartDate: getStartDate(timezone, 14, 'day').startOf('day'),
    displayEndDate: getStartDate(timezone, 8, 'day').endOf('day'),
  };
};

interface IFindCompareDateArgs {
  timezone: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  rangeSelected: string;
}

export const findCompareStartDate = ({
  timezone,
  startDate,
  endDate,
  rangeSelected,
}: IFindCompareDateArgs): moment.Moment => {
  const calStartDate = cloneDeep(startDate);
  const calEndDate = cloneDeep(endDate);
  if (rangeSelected === THIS_WEEK_SUN_TODAY || rangeSelected === LAST_WEEK) {
    return moment(calStartDate).tz(timezone).subtract(1, 'week');
  } else if (rangeSelected === LAST_MONTH || rangeSelected === THIS_MONTH) {
    return moment(calStartDate).tz(timezone).subtract(1, 'month');
  } else {
    return moment(calStartDate)
      .tz(timezone)
      .subtract(1 + calEndDate.diff(calStartDate, 'days'), 'days')
      .startOf('day');
  }
};

export const findCompareEndDate = ({
  timezone,
  startDate,
  endDate,
  rangeSelected,
}: IFindCompareDateArgs): moment.Moment => {
  const calEndDate = cloneDeep(endDate);
  const calStartDate = cloneDeep(startDate);
  if (rangeSelected === THIS_WEEK_SUN_TODAY || rangeSelected === LAST_WEEK) {
    return moment(calEndDate).tz(timezone).subtract(1, 'week');
  } else if (rangeSelected === LAST_MONTH || rangeSelected === THIS_MONTH) {
    return moment(calEndDate).tz(timezone).subtract(1, 'month');
  } else {
    return moment(calStartDate).tz(timezone).subtract(1, 'days').endOf('day');
  }
};

export const generateCalendarQuickSelectorOptions = (
  timezone: string,
): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: YESTERDAY,
      tKey: 'Yesterday',
      date1: () => moment().tz(timezone).subtract(1, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: THIS_WEEK_SUN_YESTERDAY,
      tKey: 'ThisWeekSunToYesterday',
      date1: () => moment().tz(timezone).subtract(moment().tz(timezone).format('d'), 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_7_DAYS,
      tKey: 'LastSevenDays',
      date1: () => moment().tz(timezone).subtract(7, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_WEEK,
      tKey: 'LastWeekSunToSat',
      date1: () =>
        moment()
          .tz(timezone)
          .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 7, 'days'),
      date2: () =>
        moment()
          .tz(timezone)
          .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 1, 'days'),
    },
    {
      title: THIS_MONTH,
      tKey: 'ThisMonth',
      date1: () => moment().tz(timezone).startOf('month'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_30_DAYS,
      tKey: 'LastThirtyDays',
      date1: () => moment().tz(timezone).subtract(30, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_MONTH,
      tKey: 'LastMonth',
      date1: () => moment().tz(timezone).subtract(1, 'months').date(1),
      date2: () =>
        moment()
          .tz(timezone)
          .subtract(1, 'months')
          .date(moment().tz(timezone).subtract(1, 'months').daysInMonth()),
    },
  ];
};

export const generateCalendarQuickSelectorCustomOptions = (
  timezone: string,
): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: ' Days Up To Yesterday',
      tKey: 'DaysUpToYesterday',
      date1: (e: number | string) => moment().tz(timezone).subtract(e, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
  ];
};

interface IGenerateCompareQuickSelectorOptionsArgs {
  timezone: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  rangeSelected: string;
}

export const generateCompareQuickSelectorOptions = ({
  timezone,
  startDate,
  endDate,
  rangeSelected,
}: IGenerateCompareQuickSelectorOptionsArgs): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: 'Previous Period',
      date1: () =>
        findCompareStartDate({
          timezone,
          startDate,
          endDate,
          rangeSelected,
        }),
      date2: () =>
        findCompareEndDate({
          timezone,
          startDate,
          endDate,
          rangeSelected,
        }),
    },
    {
      title: 'Last Month',
      date1: () => moment(startDate).tz(timezone).subtract(1, 'months'),
      date2: () => moment(endDate).tz(timezone).subtract(1, 'months'),
    },
  ];
};

interface ICompareQuickSelectorCustomOptionsArgs {
  timezone: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export const generateCompareQuickSelectorCustomOptions = ({
  timezone,
  startDate,
  endDate,
}: ICompareQuickSelectorCustomOptionsArgs): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: ' Days Ago, Same Period',
      tKey: 'DaysAgoSamePeriod',
      date1: (value: string) =>
        moment(startDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
      date2: (value: string) => moment(endDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
    },
    {
      title: ' Weeks Ago, Same Period',
      tKey: 'WeeksAgoSamePeriod',
      date1: (value: string) =>
        moment(startDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
      date2: (value: string) => moment(endDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
    },
    {
      title: ' Months Ago, Same Period',
      tKey: 'MonthsAgoSamePeriod',
      date1: (value: string) =>
        moment(startDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
      date2: (value: string) =>
        moment(endDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
    },
  ];
};

// Helper function to compare moment values (including null)
export const isSameMoment = (a: moment.Moment | null, b: moment.Moment | null): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.isSame(b);
};
